import { motion, AnimatePresence, useScroll } from "motion/react";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import Logo from "./Logo";

interface NavbarProps {
  isDark: boolean;
  setIsDark: (val: boolean) => void;
  setIsContactModalOpen: (val: boolean) => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (val: boolean) => void;
}

export default function Navbar({
  setIsContactModalOpen,
  isSidebarOpen,
  setIsSidebarOpen,
}: NavbarProps) {
  const location = useLocation();
  const { scrollY } = useScroll();
  const isHome = location.pathname === "/";
  const isServicePage =
    location.pathname.startsWith("/services") ||
    location.pathname === "/technology";
  const [isCompact, setIsCompact] = useState(!isHome);
  const [isVisible, setIsVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (!isServicePage) {
      setIsVisible(true);
      return;
    }

    let prevY = window.scrollY;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < 60) {
        setIsVisible(true);
      } else if (currentY > prevY && currentY - prevY > 6) {
        setIsVisible(false);
      } else if (currentY < prevY && prevY - currentY > 6) {
        setIsVisible(true);
      }
      prevY = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isServicePage]);

  useEffect(() => {
    const threshold = window.innerHeight * 0.05;
    const unsubscribe = scrollY.on("change", (y) => {
      setIsScrolled(y > 30);
      if (location.pathname === "/") {
        setIsCompact(y > threshold);
      } else {
        setIsCompact(true);
      }
    });
    return () => unsubscribe();
  }, [location]);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/technology", label: "Services" },
    { path: "/work", label: "Work" },
    { path: "/blog", label: "Blog" },
  ];

  return (
    <>
      <motion.nav
        animate={{
          y: isVisible ? 0 : -120,
        }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 w-full z-[70] px-4 py-4 transition-colors duration-500 pointer-events-none"
      >
        <motion.div
          className="mx-auto flex w-full items-center justify-between md:justify-center pointer-events-auto"
          animate={{
            maxWidth: isCompact
              ? "min(1280px, calc(100vw - 2rem))"
              : "min(1280px, calc(100vw - 2rem))",
          }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="hidden md:flex w-full items-center justify-between gap-8 rounded-full bg-white/10 dark:bg-white/5 px-3 py-2 text-white shadow-[0_18px_55px_rgba(0,0,0,0.18)] ring-1 ring-white/15 backdrop-blur-2xl">
            <Link
              to="/"
              className="interactive flex items-center gap-3 rounded-full pr-1"
              aria-label="Kivas home"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white">
                <img
                  src="/Frame8.png"
                  alt="Kivas Logo"
                  className="h-14 w-14 object-contain"
                />
              </span>
            </Link>

            <div className="flex items-center gap-1 font-Montserrat text-sm font-medium">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-full transition-all duration-300 interactive ${
                    location.pathname === link.path
                      ? "bg-white/10 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <button
              onClick={() => setIsContactModalOpen(true)}
              type="button"
              className="interactive font-Montserrat text-sm font-semibold bg-white text-black px-7 py-4 rounded-full hover:bg-white/90 active:scale-[0.97] transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] cursor-pointer"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Header */}
          <div className="flex w-full items-center justify-between md:hidden pointer-events-auto">
            <AnimatePresence>
              {!isScrolled && (
                <motion.div
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link to="/" className="interactive flex items-center">
                    <Logo />
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="ml-auto flex items-center">
              <button
                onClick={() => setIsSidebarOpen(true)}
                type="button"
                className="w-10 h-10 flex flex-col justify-center items-center gap-1.5 bg-black/5 dark:bg-white/5 backdrop-blur-md rounded-full interactive cursor-pointer active:scale-95 transition-transform"
                aria-label="Open menu"
              >
                <div className="w-5 h-0.5 bg-black dark:bg-white rounded-full" />
                <div className="w-5 h-0.5 bg-black dark:bg-white rounded-full" />
                <div className="w-5 h-0.5 bg-black dark:bg-white rounded-full" />
              </button>
            </div>
          </div>
        </motion.div>
      </motion.nav>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSidebarOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-60 md:hidden"
            />

            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-80 bg-[#fcfcfc] dark:bg-[#050505] z-70 md:hidden flex flex-col shadow-2xl"
            >
              <div className="p-8 flex justify-between items-center border-b border-black/5 dark:border-white/5">
                <Logo />
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  type="button"
                  className="w-10 h-10 flex items-center justify-center bg-black/5 dark:bg-white/5 rounded-full interactive"
                  aria-label="Close menu"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 flex flex-col gap-8">
                <nav className="flex flex-col gap-4">
                  <p className="font-Montserrat text-[10px] uppercase tracking-widest text-black/60 dark:text-white/60 mb-2">
                    Navigation
                  </p>
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsSidebarOpen(false)}
                      className={`text-3xl font-Montserrat font-bold tracking-tight transition-colors ${
                        location.pathname === link.path
                          ? "text-indigo-600 dark:text-indigo-400"
                          : "text-black dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>

                <div className="mt-auto flex flex-col gap-4">
                  <button
                    onClick={() => {
                      setIsContactModalOpen(true);
                      setIsSidebarOpen(false);
                    }}
                    type="button"
                    className="w-full font-Montserrat text-xs font-semibold uppercase tracking-widest bg-black dark:bg-white text-white dark:text-black px-8 py-5 rounded-2xl hover:opacity-90 active:scale-[0.98] transition-all duration-300 interactive flex items-center justify-center gap-3 cursor-pointer"
                  >
                    Contact Us
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                  </button>
                </div>
              </div>

              <div className="p-8 border-t border-black/5 dark:border-white/5 text-center">
                <p className="font-Montserrat text-[10px] font-semibold uppercase tracking-widest text-black/30 dark:text-white/30">
                  © 2026 KIVAS SYSTEMS
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
